import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Form, Select, InputNumber, Button, Space, Spin, Switch, message } from 'antd';
import { api } from '../api/http';
import { useAuth } from '../auth/AuthProvider';
import '../styles.css';

export default function MaintenanceSettingsCard({ customerId }) {
  const { session } = useAuth();
  const isCustomerRole = useMemo(
    () => String(session?.role || '').toLowerCase() === 'customer',
    [session]
  );

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  const load = useCallback(async () => {
    if (!customerId) return;
    setSettingsLoaded(false);
    setLoading(true);
    try {
      const { data } = await api.get(
        `/api/customers/${customerId}/maintenance-settings`
      );
      form.setFieldsValue({
        serviceIntervalType: data?.serviceIntervalType || 'DAYS',
        serviceIntervalValue: Number(data?.serviceIntervalValue ?? 90),
        dueSoonWindow: Number(data?.dueSoonWindow ?? 30),
        upcomingWindow: Number(data?.upcomingWindow ?? 60),
        blockOperatorUseWhenServiceOverdue: Boolean(
          data?.blockOperatorUseWhenServiceOverdue ?? false
        ),
      });
      setSettingsLoaded(true);
    } catch {
      message.error('Unable to load maintenance settings');
    } finally {
      setLoading(false);
    }
  }, [customerId, form]);

  useEffect(() => {
    load();
  }, [load]);

  const onSave = async () => {
    try {
      if (!settingsLoaded) {
        message.error('Load maintenance settings before saving');
        return;
      }
      const values = await form.validateFields();
      setSaving(true);
      await api.put(`/api/customers/${customerId}/maintenance-settings`, {
        serviceIntervalType: values.serviceIntervalType,
        serviceIntervalValue: Number(values.serviceIntervalValue),
        dueSoonWindow: Number(values.dueSoonWindow),
        upcomingWindow: Number(values.upcomingWindow),
        blockOperatorUseWhenServiceOverdue: Boolean(
          values.blockOperatorUseWhenServiceOverdue
        ),
      });
      message.success('Maintenance settings saved');
      await load();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (!isCustomerRole) return null;

  return (
    <Spin spinning={loading}>
      <div className="maintenance-settings-panel">
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            serviceIntervalType: 'DAYS',
            serviceIntervalValue: 90,
            dueSoonWindow: 30,
            upcomingWindow: 60,
            blockOperatorUseWhenServiceOverdue: false,
          }}
          className="maintenance-settings-form"
        >
          <div className="maintenance-settings-grid">
            <section className="maintenance-settings-section-panel maintenance-settings-interval-panel">
              <h3 className="maintenance-settings-section-title">Service interval rules</h3>

              <Form.Item
                name="serviceIntervalType"
                label="Interval Type"
                rules={[{ required: true }]}
              >
                <Select
                  options={[
                    { value: 'DAYS', label: 'Days' },
                    { value: 'HOURS', label: 'Hour Meter' },
                  ]}
                  className="settings-select-pill"
                />
              </Form.Item>

              <Form.Item
                name="serviceIntervalValue"
                label="Interval Value"
                rules={[
                  { required: true, message: 'Required' },
                  ({ getFieldValue }) => ({
                    validator(_, v) {
                      const t = getFieldValue('serviceIntervalType');
                      if (t === 'DAYS' && Number(v) >= 1) return Promise.resolve();
                      if (t === 'HOURS' && Number(v) >= 1) return Promise.resolve();
                      return Promise.reject(new Error('Must be at least 1'));
                    },
                  }),
                ]}
              >
                <InputNumber
                  min={1}
                  precision={0}
                  className="settings-input-pill"
                />
              </Form.Item>

              <Form.Item
                name="dueSoonWindow"
                label="Due Soon Window"
                rules={[
                  { required: true, message: 'Required' },
                  { type: 'number', min: 0, transform: (v) => Number(v) },
                ]}
              >
                <InputNumber
                  min={0}
                  precision={0}
                  className="settings-input-pill"
                />
              </Form.Item>

              <Form.Item
                name="upcomingWindow"
                label="Upcoming Window"
                dependencies={['dueSoonWindow']}
                rules={[
                  { required: true, message: 'Required' },
                  ({ getFieldValue }) => ({
                    validator(_, v) {
                      const ds = Number(getFieldValue('dueSoonWindow'));
                      if (Number(v) >= ds) return Promise.resolve();
                      return Promise.reject(new Error('Must be ≥ Due Soon'));
                    },
                  }),
                ]}
              >
                <InputNumber
                  min={0}
                  precision={0}
                  className="settings-input-pill"
                />
              </Form.Item>
            </section>

            <section className="maintenance-settings-section-panel maintenance-settings-operator-panel">
              <h3 className="maintenance-settings-section-title">Operator enforcement</h3>

              <Form.Item
                name="blockOperatorUseWhenServiceOverdue"
                label="Block Operator Use When Service Is Overdue"
                valuePropName="checked"
                className="maintenance-settings-toggle-item"
              >
                <Switch />
              </Form.Item>
            </section>
          </div>

          <div className="dashboard-modal-actions maintenance-settings-actions">
            <Space wrap>
              <Button
                type="primary"
                onClick={onSave}
                loading={saving}
                disabled={!settingsLoaded}
                className="dashboard-primary-action-button fleet-add-button"
              >
                Save
              </Button>
              <Button
                onClick={load}
                disabled={saving}
                className="dashboard-secondary-action-button forklift-refresh-btn"
              >
                Reset
              </Button>
            </Space>
          </div>
        </Form>
      </div>
    </Spin>
  );
}
